import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { ContractHistory } from './ContractHistory';
import type { SdpStatusEntry } from '../../api/contractStatus';
import { formatDDMMYYYY } from '../../utils/dateUtils';

export interface ContractDetailsProps {
  // Loosely-typed bag read through null guards; the source rows are untyped (see PortfolioServices).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  service: Record<string, any>;
  statusEntry?: SdpStatusEntry;
  // Basic (commodity) services carry no direction field; the caller knows which
  // list the row came from and passes it in (FT-4991).
  direction?: 'Injection' | 'Offtake';
}

delete (L.Icon.Default.prototype as unknown as { _getIconUrl: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const SECTION_HEADING = 'mb-2 text-lg font-semibold text-primary-700 dark:text-white';
const SECTION_DIVIDER = 'mt-6 border-t border-gray-200 dark:border-gray-700 pt-6';

const ContractDetails: React.FC<ContractDetailsProps> = ({ service, statusEntry, direction }) => {
  // Flex objects carry a flex SDP id; commodity (injection/offtake) objects
  // never do — the reliable flex/basic discriminator (product_name now exists
  // on both, FT-4685/FT-4991).
  const isFlexType = 'sdp_flex_id' in service;

  // asset_specs arrives as a JSON string or object depending on the endpoint
  const specs: Record<string, unknown> = (() => {
    const raw = service.asset_specs;
    if (!raw) return {};
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : (raw as Record<string, unknown>);
    } catch {
      return {};
    }
  })();
  const spec = (k: string): string | undefined => {
    const v = specs[k];
    return v == null || v === '' ? undefined : String(v);
  };

  // DB decimals arrive as "40.00000" — render as plain numbers
  const num = (v: unknown): string | undefined => {
    const n = Number(v);
    return v == null || v === '' || Number.isNaN(n) ? undefined : String(n);
  };
  const val = (v: unknown): string | undefined =>
    v == null || v === '' ? undefined : String(v);

  const startDate = service.startDate || service.start_date;
  const rawEndDate = service.endDate || service.end_date;
  const endDate =
    rawEndDate && rawEndDate !== '0000-00-00' && !Number.isNaN(new Date(rawEndDate).getTime())
      ? rawEndDate
      : null;

  const partnerRevenue = service.partner_revenue;
  const partnerFeeValue =
    partnerRevenue === 'PROFIT_SHARE'
      ? num(service.partner_profit_share)
        ? `${num(service.partner_profit_share)} %`
        : undefined
      : partnerRevenue === 'PARTNER_FEE'
        ? num(service.partner_fee)
          ? `${num(service.partner_fee)} €/MWh`
          : undefined
        : 'None';

  const addressStreet = service.addressStreet || service.address_street || '';
  const addressNumber = service.addressNumber || service.address_number || '';
  const addressCity = service.addressCity || service.address_city || '';
  const addressZip = service.addressZip || service.address_zip || '';

  const fullAddress =
    [[addressStreet, addressNumber].filter(Boolean).join(' '), [addressZip, addressCity].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(', ') || undefined;

  const latitude = parseFloat(service.latitude);
  const longitude = parseFloat(service.longitude);
  const hasLocation = !!latitude && !!longitude && !isNaN(latitude) && !isNaN(longitude);

  return isFlexType ? (
    <>
      <div className={SECTION_HEADING}>Site</div>
      <dl className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2'>
        <InfoRow label='Site ID' value={val(service.id)} />
        <InfoRow label='Site name' value={service.name} />
      </dl>

      <div className={`${SECTION_DIVIDER} ${SECTION_HEADING}`}>Asset</div>
      <dl className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2'>
        <InfoRow label='Service ID' value={val(service.sdp_flex_id) ?? val(service.sdp_flex_installation_id)} />
        <InfoRow label='Service type' value={service.asset_type === 'e-boiler' ? 'E-boiler' : service.asset_type} />
        <InfoRow label='Asset ID' value={val(service.iot_device_id)} />
        <InfoRow
          label='Nominal power'
          value={num(spec('nominal_power')) ? `${num(spec('nominal_power'))} kW` : undefined}
        />
        {Number(spec('nominal_energy')) > 0 && (
          <InfoRow
            label='Nominal energy'
            value={num(spec('nominal_energy')) ? `${num(spec('nominal_energy'))} kWh` : undefined}
          />
        )}
      </dl>

      <div className={`${SECTION_DIVIDER} ${SECTION_HEADING}`}>Current contract</div>
      <dl className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2'>
        <InfoRow label='Contract ID' value={val(service.contract_flex_id)} />
        <InfoRow label='Contract status' value={service.contract_status} />
        <InfoRow label='Start date' value={formatDDMMYYYY(service.start_date)} />
        <InfoRow label='Signature date' value={formatDDMMYYYY(service.signature_date)} />
        <InfoRow label='Commencement date' value={formatDDMMYYYY(service.commencement_date)} />
        <InfoRow
          label='End date'
          value={endDate ? formatDDMMYYYY(endDate) : 'automatically renewed monthly'}
        />
        <InfoRow
          label='Yuso fixed fee'
          value={num(service.steering_fee_fixed) ? `${num(service.steering_fee_fixed)} €/month` : undefined}
        />
        <InfoRow
          label='Yuso profit share'
          value={num(service.yuso_profit_share) ? `${num(service.yuso_profit_share)} %` : undefined}
        />
        <InfoRow label='Partner fee' value={partnerFeeValue} />
      </dl>

      {statusEntry && <ContractHistory entry={statusEntry} />}
    </>
  ) : (
    <>
      <div className={SECTION_HEADING}>Site</div>
      <dl className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2'>
        <InfoRow label='Site ID' value={val(service.site_id)} />
        <InfoRow label='Site name' value={service.site_name} />
        <InfoRow label='EAN' value={service.ean} />
        <InfoRow label='Address' value={fullAddress} />
        {direction && <InfoRow label='Direction' value={direction} />}
      </dl>

      <div className={`${SECTION_DIVIDER} ${SECTION_HEADING}`}>Meter</div>
      <dl className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2'>
        <InfoRow label='Status' value={service.sdp_status} />
      </dl>

      <div className={`${SECTION_DIVIDER} ${SECTION_HEADING}`}>Current contract</div>
      <dl className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2'>
        <InfoRow label='Contract ID' value={val(service.contract_id)} />
        <InfoRow label='Start date' value={formatDDMMYYYY(startDate)} />
        <InfoRow label='Signature date' value={formatDDMMYYYY(service.signatureDate)} />
        {endDate && <InfoRow label='End date' value={formatDDMMYYYY(endDate)} />}
        {service.fixedFee != null && (
          <InfoRow
            label='Fixed fee'
            value={num(service.fixedFee) ? `${num(service.fixedFee)} €/MWh` : undefined}
          />
        )}
        {service.imbCostPct != null && (
          <InfoRow label='Solar Imbalance Index' value={num(service.imbCostPct)} />
        )}
        {service.windImbCostPct != null && (
          <InfoRow label='Wind Imbalance Index' value={num(service.windImbCostPct)} />
        )}
        {service.product_name && <InfoRow label='Product Name' value={service.product_name} />}
      </dl>

      {statusEntry && <ContractHistory entry={statusEntry} />}

      {/* Map — commodity only, and only when coordinates are available.
          No "location not available" placeholder (FT-4991). */}
      {hasLocation && (
        <div className='mt-8'>
          <div className={SECTION_HEADING}>Location</div>
          <div className='h-56 w-full rounded-md overflow-hidden border border-primary-100 dark:border-primary-800'>
            <MapContainer
              center={[latitude, longitude]}
              zoom={16}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
                url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
              />
              <Marker position={[latitude, longitude]}>
                <Popup>
                  {addressStreet} {addressNumber} {addressCity}
                </Popup>
              </Marker>
            </MapContainer>
          </div>
          {(addressStreet || addressNumber || addressCity) && (
            <div className='text-xs text-primary-400 mt-2 text-center'>
              {addressStreet} {addressNumber} {addressCity}
            </div>
          )}
        </div>
      )}
    </>
  );
};

// Minimalistic info row
function InfoRow({ label, value }: { label: string; value?: string }) {
  return (
    <>
      <dt className='text-xs text-primary-500 font-semibold dark:text-primary-300'>{label}</dt>
      <dd className='text-sm text-primary-500 dark:text-primary-100 mb-2'>
        {value ? value : <span className='italic text-primary-400 dark:text-primary-300'>Not available</span>}
      </dd>
    </>
  );
}

export default ContractDetails;
